import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import ChatHistoryService from '../services/chatHistoryService';
import ChatSessionService from '../services/chatSessionService';

export const useChatState = (options = {}) => {
  const { mobileScrollContainerRef } = options;
  const { isAuthenticated } = useAuth();
  
  // Core chat state
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [businessType, setBusinessType] = useState('');
  
  // Session state
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  
  // Refs
  const messagesEndRef = useRef(null);
  const chatMessagesRef = useRef(null);
  
  // Scroll state
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [userHasScrolledUp, setUserHasScrolledUp] = useState(false);
  
  // Scroll to bottom function
  const scrollToBottom = () => {
    setUserHasScrolledUp(false);
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth", block: "end", inline: "nearest" });
    }
    const el = (typeof window !== 'undefined' && window.innerWidth <= 768 && mobileScrollContainerRef?.current)
      ? mobileScrollContainerRef.current
      : chatMessagesRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }
  };
  
  // Auto-scroll to bottom only when new messages are added and user hasn't manually scrolled up
  useEffect(() => {
    if (messages.length > 0 && !userHasScrolledUp) {
      const el = (typeof window !== 'undefined' && window.innerWidth <= 768 && mobileScrollContainerRef?.current)
        ? mobileScrollContainerRef.current
        : chatMessagesRef.current;
      if (el) {
        const { scrollTop, scrollHeight, clientHeight } = el;
        const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100;
        if (isNearBottom) {
          setTimeout(() => scrollToBottom(), 100);
        }
      }
    }
  }, [messages.length, scrollToBottom, userHasScrolledUp]);

  // Scroll event listener (attach to the element that actually scrolls)
  useEffect(() => {
    const el = (typeof window !== 'undefined' && window.innerWidth <= 768 && mobileScrollContainerRef?.current)
      ? mobileScrollContainerRef.current
      : chatMessagesRef.current;
    if (!el) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 20;
      setShowScrollButton(!isAtBottom);
      setUserHasScrolledUp(!isAtBottom);
    };

    handleScroll();
    el.addEventListener('scroll', handleScroll);
    return () => el.removeEventListener('scroll', handleScroll);
  }, [messages, isLoading]);
  
  // New chat function - clears state so next message creates a new conversation
  const handleNewChat = async () => {
    try {
      // Clear all state - this makes the chatbox empty
      // Empty chatbox = new conversation, so backend will create it when first message is sent
      setMessages([]);
      setInputMessage('');
      setBusinessType('');
      setCurrentConversationId(null);
      setCurrentSessionId(null);
      
      // Clear from localStorage as well
      localStorage.removeItem('currentConversationId');
      localStorage.removeItem('currentSessionId');
      
      console.log('✅ New chat started - cleared all state. Next message will create new conversation.');
    } catch (error) {
      console.error('Error in handleNewChat:', error);
      // Still clear state even if there's an error
      setMessages([]);
      setInputMessage('');
      setBusinessType('');
      setCurrentConversationId(null);
      setCurrentSessionId(null);
    }
  };
  
  return {
    // State
    messages,
    setMessages,
    inputMessage,
    setInputMessage,
    isLoading,
    setIsLoading,
    businessType,
    setBusinessType,
    currentSessionId,
    setCurrentSessionId,
    currentConversationId,
    setCurrentConversationId,
    showScrollButton,
    
    // Refs
    messagesEndRef,
    chatMessagesRef,
    
    // Functions
    scrollToBottom,
    handleNewChat
  };
};
